/**
 * WHAT THE PHOTOGRAPHS IN THIS BRIEF ACTUALLY ARE.
 *
 * WHY THIS EXISTS
 *   In a COTALITY-ONLY workflow there is no MLS file to read photographs out of, so every picture
 *   in the brief is filled by `amb_build._fill_missing_photos_from_streetview` — a public Google
 *   Street View image of each property's own address. That is a good outcome: a brief with houses
 *   in it reads as a brief, and the alternative was a page of grey placeholders. It is also a claim
 *   the agent is about to put their name on, and until this component landed they were never told.
 *
 *   `_fill_missing_photos_from_streetview` DOES write the disclosure — into `data.checklist`. That
 *   key is the PREPARER's checklist and has had no client-facing renderer since `992178b`, removed
 *   on the recorded objection that "the deliverable had started reading like a work order". That
 *   objection was about the DELIVERABLE. This is the agent's own review screen, which is exactly
 *   where a work-order note belongs — so this reads the same facts off the payload directly rather
 *   than resurrecting the checklist in the document.
 *
 * IT COUNTS, IT DOES NOT ASSUME
 *   Every number here is counted from `data.cards` and `data.hero`, so a mixed brief — some photos
 *   from the file, some from Street View, one comp with none — describes itself accurately. There
 *   is no "is this a Cotality job" flag anywhere in the payload and this does not invent one: the
 *   Cotality-only case is simply the one where the Street View count equals the property count.
 *
 * SILENT WHEN THERE IS NOTHING TO SAY
 *   A brief whose photographs all came from the agent's own file renders nothing. A notice that
 *   appears on every report is a notice nobody reads, and the one time it matters it would look
 *   the same as every other time.
 */
import type { AmbReportData } from "./types";
import { themeVar as colors } from "../design-tokens";

/** `amb_build.STREETVIEW_PHOTO_STATUS`. */
const STREETVIEW = "streetview";

export type PhotoSourceSummary = {
  /** Comparables whose photograph came from Street View. */
  streetviewComps: number;
  /** True when the subject's hero photograph came from Street View. */
  streetviewSubject: boolean;
  /** Comparables shown with no photograph at all. */
  missing: number;
  /** The distinct stated reasons for those gaps, in payload order, deduplicated. */
  reasons: string[];
  /** Google's credit string, as the deliverable will print it. */
  attribution: string;
  /** True when EVERY photograph in the brief is Street View's — the Cotality-only shape. */
  allStreetview: boolean;
};

/** Exported for the test: the counting is the part worth asserting, not the markup. */
export function summarizePhotoSources(data: AmbReportData): PhotoSourceSummary {
  const cards = Array.isArray(data?.cards) ? data.cards : [];
  const withPhoto = cards.filter((c) => !!c?.photo);
  const streetviewComps = withPhoto.filter((c) => c?.photo_status === STREETVIEW).length;
  const streetviewSubject = data?.subject_photo_source === STREETVIEW && !!data?.hero?.subject_photo;

  const reasons: string[] = [];
  for (const c of cards) {
    if (c?.photo) continue;
    const r = (c?.photo_omitted_reason || "").trim();
    if (r && !reasons.includes(r)) reasons.push(r);
  }

  return {
    streetviewComps,
    streetviewSubject,
    missing: cards.length - withPhoto.length,
    reasons,
    // The provider's own string, never a copy written here — a credit this file invented could
    // drift from the one the PDF prints, and the agent would be shown a different claim.
    attribution: (data?.photo_attribution || "").trim(),
    allStreetview:
      withPhoto.length > 0 && streetviewComps === withPhoto.length && streetviewComps === cards.length,
  };
}

export function PhotoSourceNote({ data }: { data: AmbReportData }) {
  const s = summarizePhotoSources(data);
  if (!s.streetviewComps && !s.streetviewSubject && !s.reasons.length) return null;

  const shown: string[] = [];
  if (s.streetviewSubject) shown.push("your home");
  if (s.streetviewComps) {
    shown.push(`${s.streetviewComps} ${s.streetviewComps === 1 ? "comparable" : "comparables"}`);
  }
  // One sentence, agreeing with itself. Built here rather than as a chain of ternaries inside the
  // markup: a plural/singular decision threaded through five inline conditionals is how "The
  // photograph for your home and 2 comparables IS a public image" gets shipped.
  const many = s.allStreetview || s.streetviewComps > 1 || shown.length > 1;
  const subject = s.allStreetview
    ? "Every photograph in this brief is a public Google Street View image"
    : many
      ? `The photographs for ${shown.join(" and ")} are public Google Street View images`
      : `The photograph for ${shown.join(" and ")} is a public Google Street View image`;
  const rest = many
    ? ", fetched against each property’s own address because the source file carried none for" +
      " them. They are reference pictures, not evidence from your file — upload your own where" +
      " you have them and they take precedence."
    : ", fetched against that property’s own address because the source file carried none for" +
      " it. It is a reference picture, not evidence from your file — upload your own where you" +
      " have one and it takes precedence.";

  return (
    <div
      style={{
        marginTop: 16,
        // NO LOCAL WIDTH CAP. Same reason as the layout picker above it — the page owns the
        // measure (`AppShell content="prose"` = 880px), and a 520px block beside 880px siblings
        // reads as a different product. See ui/measures.ts.
        padding: "13px 15px",
        borderRadius: 8,
        border: `1px solid ${colors.border}`,
        background: "#FAFBFC",
        fontSize: 13.5,
        lineHeight: 1.55,
        color: colors.textSecondary,
      }}
    >
      <div style={{ fontWeight: 600, color: colors.textPrimary, marginBottom: 4 }}>
        Where the photographs came from
      </div>

      {shown.length > 0 && (
        <p style={{ margin: "0 0 6px" }}>
          {/* NAMED PLAINLY. "Reference imagery" is a phrase that lets an agent assume it means
              something more official than it is; the sentence says whose picture it is, that it was
              fetched by the property's own address, and that their own photograph replaces it. */}
          {subject}
          {rest}
        </p>
      )}

      {s.attribution && (
        <p style={{ margin: "0 0 6px" }}>
          The brief prints “{s.attribution}” {many ? "beside each of them" : "beside it"}, which
          Google&rsquo;s terms require.
        </p>
      )}

      {s.reasons.length > 0 && (
        <p style={{ margin: 0 }}>
          {/* THE GAP IS NAMED, WITH ITS REASON AND ITS REMEDY. `_streetview_resolvable` refuses a
              bare street line because four of eight measured against the live provider resolved to
              a different state. That refusal is the product working; it should not look like a
              missing feature. */}
          {s.missing} {s.missing === 1 ? "comparable has" : "comparables have"} no photograph:{" "}
          {s.reasons.join("; ")}. A street line with no city or ZIP matches houses in other states,
          so we would rather show none than the wrong house. Add the city and ZIP, or attach your
          own photograph.
        </p>
      )}
    </div>
  );
}

export default PhotoSourceNote;
